import React, { FC, useCallback, useState } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { Link, RouterUtils } from 'application/providers/RouterProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { POINTS_PAGE_PATH } from 'application/consts';
import useChangePricePlanPeriodModalViewModel from './useChangePricePlanPeriodModalViewModel';
import { PricePlanPeriodList } from './components';
import { SelectedPricePlanPeriodListItem } from './components/PricePlanPeriodList/types';
import TotalPriceBlock from '../TotalPriceBlock';
import RatesTable from '../RatesTable';

import { TEXTS } from './texts';

import styles from './index.module.scss';

const ChangePricePlanPeriodModal: FC<ModalProps[ModalTypes.BILLING_CHANGE_PRICE_PLAN_PERIOD]> = ({
  openModal,
  close,
}) => {
  const {
    currentPricePlan,
    currentPeriod,
    renewableLicensesCount,
    totalLicensesCount,
    isPrimeDiscountAvailable,
    changePricePlanPeriod,
    isSubmitButtonLoading,
    isCancelButtonLoading,
    hasInactiveLicenses,
  } = useChangePricePlanPeriodModalViewModel();

  const notification = useNotificationContext();

  const [selectedPricePlanPeriodItem, setSelectedPricePlanPeriodItem] = useState<SelectedPricePlanPeriodListItem>({
    period: currentPeriod || { id: '', title: '', payFrequencyMonth: 1 },
    rows: currentPeriod ? currentPeriod.rows : [],
  });

  const onPricePlanPeriodChange = (pricePlanPeriod: SelectedPricePlanPeriodListItem) => {
    if (isPrimeDiscountAvailable) {
      return;
    }

    setSelectedPricePlanPeriodItem(pricePlanPeriod);
  };

  const onChangePricePlan = useCallback(async () => {
    try {
      const isOk = await changePricePlanPeriod(selectedPricePlanPeriodItem.period.id);

      if (isOk) {
        notification.showInfoNotification({
          children: TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_SUCCESS,
        });

        close();
      }
    } catch (e) {
      notification.showErrorNotification({
        children: TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_ERROR,
      });
    }
  }, [changePricePlanPeriod, selectedPricePlanPeriodItem]);

  const onChangePricePlanConfirm = useCallback(async () => {
    openModal(ModalTypes.BILLING_CHANGE_PRICE_PLAN_PERIOD_CONFIRM, {
      periodTitle: selectedPricePlanPeriodItem.period.title,
      payFrequencyMonth: selectedPricePlanPeriodItem.period.payFrequencyMonth,
      onSubmit: onChangePricePlan,
    });
  }, [onChangePricePlan, selectedPricePlanPeriodItem]);

  return (
    <ModalScreen
      title={TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_TITLE}
      onClose={close}
      contentClassName={styles.content}
      actionButtons={[
        <Button
          key="button_change_price_plan_period_modal_submit"
          elementId="button_change_price_plan_period_modal_submit"
          isLoading={isSubmitButtonLoading}
          isDisabled={selectedPricePlanPeriodItem.period.id === currentPeriod.id}
          type={ButtonType.PRIMARY}
          onClick={onChangePricePlanConfirm}>
          {TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_BUTTON_SUBMIT}
        </Button>,
        <Button
          key="button_change_price_plan_period_modal_cancel"
          elementId="button_change_price_plan_period_modal_cancel"
          type={ButtonType.SECONDARY}
          isLoading={isCancelButtonLoading}
          onClick={() => close()}>
          {TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_BUTTON_CANCEL}
        </Button>,
      ]}>
      <div className={styles.description}>{TEXTS.CHANGE_PRICE_PLAN_PERIOD_MODAL_DESCRIPTION}</div>

      <PricePlanPeriodList
        pricePlan={currentPricePlan}
        currentPeriod={currentPeriod}
        selectedPricePlanPeriodId={selectedPricePlanPeriodItem.period.id}
        licensesCount={renewableLicensesCount}
        isPrimeAvailable={isPrimeDiscountAvailable}
        onPricePlanPeriodChange={onPricePlanPeriodChange}
      />

      {hasInactiveLicenses ? (
        <div>
          <span>{TEXTS.CHANGE_PRICE_PLAN_PERIOD_TEXT_FOR_INACTIVE_STATUS}</span>
          <Link
            to={RouterUtils.generatePath(POINTS_PAGE_PATH, { id: 'all' })}
            onClick={() => close()}
            replace>
            {TEXTS.CHANGE_PRICE_PLAN_PERIOD_FIRST_DEBITING_POINT_COUNT_TEXT(totalLicensesCount)}
          </Link>
        </div>
      ) : (
        <TotalPriceBlock
          currentPeriod={currentPeriod}
          selectedPriceItemPeriodId={selectedPricePlanPeriodItem.period.id}
          selectedPriceItemPeriodTitle={selectedPricePlanPeriodItem.period.title}
        />
      )}

      <RatesTable
        className={styles.rates}
        rows={selectedPricePlanPeriodItem.rows}
        periodPayFrequencyMonth={selectedPricePlanPeriodItem.period.payFrequencyMonth}
        pricePlanTitle={currentPricePlan.title}
        pricePlanCurrency={currentPricePlan.currency}
        elementIdPrefix="change_price_plan_period_modal"
      />
    </ModalScreen>
  );
};

export default React.memo(ChangePricePlanPeriodModal);
